import { useState } from 'react';
import { ArrowLeft, Heart, MessageCircle, Share2, MoreVertical, ThumbsUp, Send, Image as ImageIcon, Smile } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardFooter, CardHeader } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { useTheme, themes } from './ThemeProvider';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { toast } from 'sonner';

interface CommunityPageProps {
  onBack: () => void;
}

interface Post {
  id: number;
  author: {
    name: string;
    avatar: string;
    verified: boolean;
    location: string;
  };
  content: string;
  image?: string;
  likes: number;
  comments: number;
  shares: number;
  timestamp: string;
  category: 'success-story' | 'advice' | 'question' | 'experience';
  liked?: boolean;
}

const samplePosts: Post[] = [
  {
    id: 1,
    author: {
      name: "Priya & Rahul",
      avatar: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=100&h=100&fit=crop",
      verified: true,
      location: "Mumbai, Maharashtra"
    },
    content: "We found each other on Atootbandan 6 months ago, and today we're getting married! Thank you for helping us find our perfect match. The AI recommendations were spot-on! 💕",
    image: "https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop",
    likes: 1245,
    comments: 89,
    shares: 234,
    timestamp: "2 hours ago",
    category: "success-story",
    liked: false
  },
  {
    id: 2,
    author: {
      name: "Anjali Sharma",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
      verified: true,
      location: "Delhi, India"
    },
    content: "Quick tip for everyone: Make sure to add recent photos and be honest in your profile. Authenticity attracts the right matches! I got 50+ quality matches in just one week. 🌟",
    likes: 892,
    comments: 45,
    shares: 156,
    timestamp: "5 hours ago",
    category: "advice"
  },
  {
    id: 3,
    author: {
      name: "Amit Patel",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop",
      verified: false,
      location: "Bangalore, Karnataka"
    },
    content: "How do you guys handle the first conversation? I'm getting matches but struggling to start meaningful conversations. Any tips would be appreciated! 🤔",
    likes: 234,
    comments: 67,
    shares: 12,
    timestamp: "8 hours ago",
    category: "question"
  },
  {
    id: 4,
    author: {
      name: "Sneha & Karthik",
      avatar: "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=100&h=100&fit=crop",
      verified: true,
      location: "Chennai, Tamil Nadu"
    },
    content: "One year anniversary of our first match on Atootbandan! Best decision we ever made was to trust the process and be patient. To everyone searching - your person is out there! ❤️",
    image: "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=600&h=400&fit=crop",
    likes: 2103,
    comments: 156,
    shares: 445,
    timestamp: "1 day ago",
    category: "success-story"
  },
  {
    id: 5,
    author: {
      name: "Meera Singh",
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop",
      verified: true,
      location: "Pune, Maharashtra"
    },
    content: "The video call feature is amazing! I had 3 virtual dates this week and it really helps to know the person better before meeting in person. Safety first! 👍",
    likes: 456,
    comments: 34,
    shares: 89,
    timestamp: "1 day ago",
    category: "experience"
  }
];

export default function CommunityPage({ onBack }: CommunityPageProps) {
  const { theme } = useTheme();
  const currentTheme = themes[theme];
  const [posts, setPosts] = useState<Post[]>(samplePosts);
  const [newPost, setNewPost] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { id: 'all', label: 'All Posts', icon: '📰' },
    { id: 'success-story', label: 'Success Stories', icon: '💑' },
    { id: 'advice', label: 'Advice', icon: '💡' },
    { id: 'question', label: 'Questions', icon: '❓' },
    { id: 'experience', label: 'Experiences', icon: '⭐' }
  ];

  const handleLike = (postId: number) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          liked: !post.liked,
          likes: post.liked ? post.likes - 1 : post.likes + 1
        };
      }
      return post;
    }));
  };

  const handleShare = (postId: number) => {
    toast.success('Post link copied to clipboard!');
  };

  const handleCreatePost = () => {
    if (!newPost.trim()) {
      toast.error('Please write something to post');
      return;
    }

    const post: Post = {
      id: Date.now(),
      author: {
        name: "You",
        avatar: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop",
        verified: true,
        location: "Your City"
      },
      content: newPost,
      likes: 0,
      comments: 0,
      shares: 0,
      timestamp: "Just now",
      category: "experience",
      liked: false
    };

    setPosts([post, ...posts]);
    setNewPost('');
    toast.success('Post created successfully!');
  };

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'success-story': return 'bg-green-100 text-green-700';
      case 'advice': return 'bg-blue-100 text-blue-700';
      case 'question': return 'bg-amber-100 text-amber-700';
      case 'experience': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={onBack}>
              <ArrowLeft className="size-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Heart className={`size-6 ${currentTheme.primaryText}`} />
              <span className={`text-xl font-bold ${currentTheme.primaryText}`}>Community</span>
            </div>
            <div className="w-20" /> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Categories */}
        <div className="mb-6 bg-white rounded-xl p-4 shadow-sm">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map(category => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category.id)}
                className={selectedCategory === category.id ? `${currentTheme.gradient} text-white` : ''}
                size="sm"
              >
                <span className="mr-2">{category.icon}</span>
                {category.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Create Post */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop" />
                <AvatarFallback>You</AvatarFallback>
              </Avatar>
              <Textarea
                placeholder="Share your story, ask a question, or give advice..."
                value={newPost}
                onChange={(e) => setNewPost(e.target.value)}
                className="flex-1 min-h-[80px]"
              />
            </div>
          </CardHeader>
          <CardFooter className="border-t pt-4 flex justify-between">
            <div className="flex gap-2">
              <Button variant="ghost" size="sm">
                <ImageIcon className="size-4 mr-2" />
                Photo
              </Button>
              <Button variant="ghost" size="sm">
                <Smile className="size-4 mr-2" />
                Emoji
              </Button>
            </div>
            <Button 
              onClick={handleCreatePost}
              className={`${currentTheme.gradient} text-white`}
              disabled={!newPost.trim()}
            >
              <Send className="size-4 mr-2" />
              Post
            </Button>
          </CardFooter>
        </Card>

        {/* Posts Feed */}
        <div className="space-y-4">
          {filteredPosts.map(post => (
            <Card key={post.id} className="overflow-hidden">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <Avatar className="size-12">
                      <AvatarImage src={post.author.avatar} />
                      <AvatarFallback>{post.author.name[0]}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{post.author.name}</span>
                        {post.author.verified && (
                          <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                            ✓ Verified
                          </Badge>
                        )}
                        <Badge className={`text-xs ${getCategoryColor(post.category)}`}>
                          {post.category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">{post.author.location}</p>
                      <p className="text-xs text-gray-400">{post.timestamp}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem>Report Post</DropdownMenuItem>
                      <DropdownMenuItem>Hide Post</DropdownMenuItem>
                      <DropdownMenuItem>Follow Author</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                <p className="text-gray-700 mb-4 leading-relaxed">{post.content}</p>
                {post.image && (
                  <img 
                    src={post.image} 
                    alt="Post"
                    className="w-full rounded-lg mb-4"
                  />
                )}
              </CardContent>

              <CardFooter className="border-t pt-4">
                <div className="flex items-center justify-between w-full">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleLike(post.id)}
                    className={post.liked ? currentTheme.primaryText : ''}
                  >
                    <ThumbsUp className={`size-4 mr-2 ${post.liked ? 'fill-current' : ''}`} />
                    {post.likes}
                  </Button>
                  <Button variant="ghost" size="sm">
                    <MessageCircle className="size-4 mr-2" />
                    {post.comments}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleShare(post.id)}
                  >
                    <Share2 className="size-4 mr-2" />
                    {post.shares}
                  </Button>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl">
            <p className="text-gray-500 text-lg">No posts in this category yet.</p>
            <p className="text-gray-400 text-sm mt-2">Be the first to share!</p>
          </div>
        )}
      </div>
    </div>
  );
}